import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { UserPlus, Trash2, Users, Shield, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';

const INPUT_CLASS = "bg-[#1A1D21] border-[#33383F] text-[#F0EDE8] placeholder:text-[#555D68] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl";

export function ManageTeamDialog({ open, onOpenChange, currentUserId }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch {
      toast.error('Failed to load team');
    }
  }, []);

  useEffect(() => { if (open) fetchUsers(); }, [open, fetchUsers]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      await api.post('/admin/employees', form);
      toast.success(`Added ${form.name}`);
      setForm({ name: '', email: '', password: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add employee');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    try {
      await api.delete(`/admin/employees/${user.id}`);
      toast.success(`Removed ${user.name}`);
      setConfirmDelete(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const handlePromote = async (user) => {
    try {
      await api.post(`/admin/employees/${user.id}/promote`);
      toast.success(`${user.name} is now an admin`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to promote');
    }
  };

  const handleDemote = async (user) => {
    try {
      await api.post(`/admin/employees/${user.id}/demote`);
      toast.success(`${user.name} is now an employee`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to demote');
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#23272E] border-[#33383F] rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#F0EDE8] font-bold tracking-tight flex items-center gap-2">
            <Users className="w-5 h-5" />Manage Team
          </DialogTitle>
        </DialogHeader>

        {/* Current team list */}
        <div className="space-y-2 mt-4">
          <Label className="text-[#9CA3AF] text-xs uppercase tracking-wide">Current Team ({users.length})</Label>
          <div className="space-y-1.5">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between bg-[#1A1D21] border border-[#33383F] rounded-xl px-3 py-2"
                data-testid={`team-member-${u.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-[#F0EDE8] font-medium truncate">
                    {u.name}
                    {u.role === 'admin' && <span className="ml-2 text-xs bg-[#4A9D6E]/20 text-[#4A9D6E] px-1.5 py-0.5 rounded">Admin</span>}
                    {u.id === currentUserId && <span className="ml-2 text-xs text-[#9CA3AF]">(you)</span>}
                  </div>
                  <div className="text-xs text-[#9CA3AF] truncate">{u.email}</div>
                </div>
                {u.id !== currentUserId && (
                  <div className="flex items-center gap-1">
                    {u.role !== 'admin' ? (
                      <Button type="button" size="icon" variant="ghost"
                        className="h-8 w-8 text-[#4A9D6E] hover:bg-[#4A9D6E]/10 hover:text-[#4A9D6E] rounded-lg"
                        onClick={() => handlePromote(u)}
                        title="Promote to admin"
                        data-testid={`promote-employee-${u.id}`}>
                        <Shield className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button type="button" size="icon" variant="ghost"
                        className="h-8 w-8 text-[#9CA3AF] hover:bg-[#2C3038] hover:text-[#F0EDE8] rounded-lg"
                        onClick={() => handleDemote(u)}
                        title="Demote to employee"
                        data-testid={`demote-employee-${u.id}`}>
                        <ShieldOff className="w-4 h-4" />
                      </Button>
                    )}
                    {u.role !== 'admin' && (
                      <Button type="button" size="icon" variant="ghost"
                        className="h-8 w-8 text-[#DC2626] hover:bg-[#DC2626]/10 hover:text-[#DC2626] rounded-lg"
                        onClick={() => setConfirmDelete(u)}
                        data-testid={`remove-employee-${u.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Add form */}
        <form onSubmit={handleAdd} className="space-y-3 pt-4 border-t border-[#33383F] mt-4">
          <Label className="text-[#9CA3AF] text-xs uppercase tracking-wide">Add Employee</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Full name" required className={INPUT_CLASS} data-testid="new-employee-name" />
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="email@example.com" required className={INPUT_CLASS} data-testid="new-employee-email" />
          <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Temporary password (min 8 chars)" required minLength={8} className={INPUT_CLASS} data-testid="new-employee-password" />
          <Button type="submit" disabled={saving}
            className="w-full bg-[#4A9D6E] hover:bg-[#3D8A5E] text-white rounded-xl"
            data-testid="add-employee-submit">
            <UserPlus className="w-4 h-4 mr-2" />{saving ? 'Adding...' : 'Add Employee'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>

    <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
      <AlertDialogContent className="bg-[#23272E] border-[#33383F] rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#F0EDE8]">Remove {confirmDelete?.name}?</AlertDialogTitle>
          <AlertDialogDescription className="text-[#9CA3AF]">
            They&apos;ll lose access immediately. Messages they sent will remain visible in conversations.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="border-[#33383F] bg-transparent text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => handleDelete(confirmDelete)}
            className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-xl"
            data-testid="confirm-remove-employee">
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
